import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import {
  Component,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  Renderer2,
  signal,
} from '@angular/core';

import { IconButtonComponent } from '../../icon-button/icon-button.component';

@Component({
  selector: 'lib-modal-base',
  imports: [IconButtonComponent, TranslateModule, CommonModule],
  templateUrl: './modal-base.component.html',
  styleUrls: ['./modal-base.component.scss'],
})
export class ModalBaseComponent implements OnDestroy {
  readonly visible = input(false);
  readonly title = input<string>('');
  readonly description = input<string>('');
  readonly backdrop = input(true);
  readonly size = input<'small' | 'large' | 'fullscreen'>('small');
  readonly closeOnClickOutside = input(true);
  readonly handleClose = output<void>();

  protected isClosing = signal(false);
  private touchStartY = 0;
  private touchDeltaY = 0;
  private isDragging = false;
  private touchMoveListener: ((e: TouchEvent) => void) | null = null;
  private touchStartListener: ((e: TouchEvent) => void) | null = null;
  private modalContentElement: HTMLElement | null = null;

  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private originalParent: Node | null = null;
  private removeListeners: (() => void)[] = [];

  constructor() {
    effect(() => {
      if (this.visible()) {
        this.moveToBody();
        this.attachTouchListeners();
      } else if (!this.isClosing()) {
        this.restorePosition();
        this.removeTouchListeners();
      }
    });
  }

  private attachTouchListeners() {
    // Wait for next tick to ensure DOM is ready
    setTimeout(() => {
      const modalContent = this.elementRef.nativeElement.querySelector('.modal-content');
      if (modalContent) {
        this.modalContentElement = modalContent;
        // Add touchstart with passive: false option for potential preventDefault
        this.touchStartListener = (e: TouchEvent) => this.onTouchStart(e);
        modalContent.addEventListener('touchstart', this.touchStartListener, { passive: false });
        // Add touchmove with passive: false option for preventDefault support
        this.touchMoveListener = (e: TouchEvent) => this.onTouchMove(e);
        modalContent.addEventListener('touchmove', this.touchMoveListener, { passive: false });
        const touchEnd = this.renderer.listen(modalContent, 'touchend', (e: TouchEvent) =>
          this.onTouchEnd(e),
        );

        this.removeListeners.push(touchEnd);
      }
    });
  }

  private removeTouchListeners() {
    this.removeListeners.forEach((remove) => remove());
    this.removeListeners = [];

    // Remove touch listeners manually
    if (this.modalContentElement) {
      if (this.touchStartListener) {
        this.modalContentElement.removeEventListener('touchstart', this.touchStartListener);
        this.touchStartListener = null;
      }
      if (this.touchMoveListener) {
        this.modalContentElement.removeEventListener('touchmove', this.touchMoveListener);
        this.touchMoveListener = null;
      }
      this.modalContentElement = null;
    }
  }

  private moveToBody() {
    const element = this.elementRef.nativeElement;
    if (!this.originalParent) {
      this.originalParent = element.parentNode;
      this.renderer.appendChild(document.body, element);
    }
  }

  private restorePosition() {
    const element = this.elementRef.nativeElement;
    if (this.originalParent) {
      this.renderer.appendChild(this.originalParent, element);
      this.originalParent = null;
    }
  }

  ngOnDestroy() {
    this.removeTouchListeners();
    this.restorePosition();
  }

  close() {
    if (this.isClosing()) return; // Prevent multiple close calls

    this.isClosing.set(true);
    // Wait for animation to complete before emitting close event
    setTimeout(() => {
      this.isClosing.set(false);
      this.handleClose.emit();
    }, 300); // Match animation duration
  }

  @HostListener('window:keydown.escape')
  onEscape() {
    this.close();
  }

  onBackdropClick() {
    if (this.backdrop() && this.closeOnClickOutside()) {
      this.close();
    }
  }

  onBackdropKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') this.close();
  }

  onTouchStart(event: TouchEvent) {
    const modalContent = event.currentTarget as HTMLElement;
    // Only start dragging if scrolled to top or touching near the top (swipe indicator area)
    const touchY = event.touches[0].clientY;
    const rect = modalContent.getBoundingClientRect();
    const touchRelativeY = touchY - rect.top;

    if (modalContent.scrollTop === 0 || touchRelativeY < 60) {
      this.touchStartY = touchY;
      this.touchDeltaY = 0;
      this.isDragging = true;
    }
  }

  onTouchMove(event: TouchEvent) {
    if (!this.isDragging) return;

    const modalContent = event.currentTarget as HTMLElement;
    this.touchDeltaY = event.touches[0].clientY - this.touchStartY;

    // Only allow downward swipe (positive deltaY) on mobile
    if (this.touchDeltaY > 0 && window.innerWidth <= 600) {
      // Prevent default scrolling when swiping down
      event.preventDefault();

      if (modalContent) {
        modalContent.style.transform = `translateY(${this.touchDeltaY}px)`;
        modalContent.style.transition = 'none';
      }
    }
  }

  onTouchEnd(event: TouchEvent) {
    if (!this.isDragging) return;

    const modalContent = event.currentTarget as HTMLElement;

    if (modalContent) {
      // Close if swiped down more than 100px on mobile
      if (this.touchDeltaY > 100 && window.innerWidth <= 600) {
        this.close();
      } else {
        // Reset transform if swipe wasn't far enough
        modalContent.style.transform = '';
        modalContent.style.transition = '';
      }
    }

    this.isDragging = false;
    this.touchDeltaY = 0;
  }
}
